import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon as Plus,
  MagnifyingGlassIcon as Search,
  PencilIcon as Edit,
  TrashIcon as Trash2,
  GlobeAltIcon as Globe,
  EnvelopeIcon as Mail,
  ServerIcon as Server,
  LinkIcon as Link,
  HashtagIcon as Hash,
  DevicePhoneMobileIcon as Smartphone,
  ExclamationTriangleIcon as AlertTriangle,
  CheckCircleIcon as CheckCircle,
  ClockIcon as Clock,
  ArchiveBoxIcon as Archive,
  ShieldCheckIcon as Shield,
  BuildingOffice2Icon as Building2,
  ArrowDownTrayIcon as Download,
} from '@heroicons/react/24/outline';
import { assetsService, Asset, AssetCreate, AssetFilters, AssetsSummary } from '../services/assets';
import { companiesService, Company } from '../services/companies';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../services/users';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';
import reportsService from '../services/reports';
import PageHeader from '../components/UI/PageHeader';

const Assets: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [summary, setSummary] = useState<AssetsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [filters, setFilters] = useState<AssetFilters>({
    skip: 0,
    limit: 50,
  });

  // Para usuários CLIENT, usar automaticamente a empresa do usuário
  const isClientUser = user?.role === UserRole.CLIENT;
  const effectiveCompanyId = isClientUser ? user?.company_id : selectedCompany;

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      const filtersWithCompany = {
        ...filters,
        company_id: effectiveCompanyId || undefined,
      };
      const assets = await assetsService.getAssets(filtersWithCompany);
      setAssets(assets);
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, effectiveCompanyId]);

  const loadSummary = useCallback(async () => {
    try {
      const summary = await assetsService.getAssetsSummary(effectiveCompanyId || undefined);
      setSummary(summary);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    }
  }, [effectiveCompanyId]);

  const loadCompanies = useCallback(async () => {
    // Para usuários CLIENT, não carregar empresas pois usarão apenas a sua própria
    if (isClientUser) {
      return;
    }

    try {
      const companies = await companiesService.getActive();
      setCompanies(companies);

      // Definir a primeira empresa como padrão se não houver empresa selecionada
      if (companies.length > 0 && selectedCompany === null) {
        setSelectedCompany(companies[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  }, [selectedCompany, isClientUser]);

  // Carregar empresas apenas uma vez na inicialização
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Carregar ativos e resumo quando empresa mudar
  useEffect(() => {
    if (effectiveCompanyId !== null) {
      loadAssets();
      loadSummary();
    }
  }, [loadAssets, loadSummary, effectiveCompanyId]);

  const handleCreateAsset = async (assetData: AssetCreate) => {
    try {
      await assetsService.createAsset(assetData);
      setShowCreateModal(false);
      loadAssets();
      loadSummary();
      // Você pode adicionar uma notificação de sucesso aqui
      console.log('Ativo criado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar ativo:', error);
      // Mostrar erro específico se disponível
      if (error.response?.data?.detail) {
        alert(`Erro: ${error.response.data.detail}`);
      } else if (error.response?.status === 422) {
        alert('Erro de validação: Verifique os dados inseridos');
      } else {
        alert('Erro ao criar ativo. Tente novamente.');
      }
    }
  };

  const handleUpdateAsset = async (id: number, assetData: any) => {
    try {
      await assetsService.updateAsset(id, assetData);
      setShowEditModal(false);
      setSelectedAsset(null);
      loadAssets();
      loadSummary();
    } catch (error) {
      console.error('Erro ao atualizar ativo:', error);
    }
  };

  const handleDeleteAsset = async (id: number) => {
    if (window.confirm('Tem certeza que deseja deletar este ativo?')) {
      try {
        await assetsService.deleteAsset(id);
        loadAssets();
        loadSummary();
      } catch (error) {
        console.error('Erro ao deletar ativo:', error);
      }
    }
  };

  const getAssetIcon = (type: Asset['type']) => {
    switch (type) {
      case 'domain':
        return <Globe className='w-4 h-4' />;
      case 'subdomain':
        return <Globe className='w-4 h-4' />;
      case 'ip_address':
        return <Server className='w-4 h-4' />;
      case 'email':
        return <Mail className='w-4 h-4' />;
      case 'url':
        return <Link className='w-4 h-4' />;
      case 'file':
        return <Hash className='w-4 h-4' />;
      case 'social_media':
        return <Smartphone className='w-4 h-4' />;
      case 'mobile_app':
        return <Smartphone className='w-4 h-4' />;
      case 'certificate':
        return <Shield className='w-4 h-4' />;
      case 'repository':
        return <Archive className='w-4 h-4' />;
      default:
        return <AlertTriangle className='w-4 h-4' />;
    }
  };

  const getStatusIcon = (status: Asset['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle className='w-4 h-4 text-green-500' />;
      case 'monitoring':
        return <Clock className='w-4 h-4 text-yellow-500' />;
      case 'inactive':
        return <AlertTriangle className='w-4 h-4 text-gray-500' />;
      case 'compromised':
        return <AlertTriangle className='w-4 h-4 text-red-500' />;
      case 'unknown':
        return <AlertTriangle className='w-4 h-4 text-gray-400' />;
      default:
        return <AlertTriangle className='w-4 h-4' />;
    }
  };

  const getCriticalityColor = (criticality: Asset['criticality']) => {
    switch (criticality) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className='p-6'>
      {/* Header */}
      <PageHeader
        title={t('assets.title')}
        subtitle={t('assets.subtitle')}
      >
        <button
          onClick={async () => {
            try {
              await reportsService.downloadAssetsEnrichmentXlsx();
            } catch (err) {
              console.error('Erro ao gerar relatório XLSX:', err);
              alert('Erro ao gerar relatório.');
            }
          }}
          className='bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700'
          title='Gerar relatório XLSX de ativos'
        >
          <Download className='w-4 h-4' />
          XLSX
        </button>
        <button
          onClick={() => setShowCreateModal(true)}
          className='bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700'
        >
          <Plus className='w-4 h-4' />
          {t('assets.new')}
        </button>
      </PageHeader>

      {/* Resumo */}
      {summary && (
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
          <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow'>
            <h3 className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('assets.stats.totalAssets')}</h3>
            <p className='text-2xl font-bold text-gray-900 dark:text-gray-100'>{summary.total_assets}</p>
          </div>
          <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow'>
            <h3 className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('assets.stats.domains')}</h3>
            <p className='text-2xl font-bold text-blue-600'>{summary.by_type.domain || 0}</p>
          </div>
          <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow'>
            <h3 className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('assets.stats.ips')}</h3>
            <p className='text-2xl font-bold text-green-600'>{summary.by_type.ip_address || 0}</p>
          </div>
          <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow'>
            <h3 className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('assets.stats.activeAssets')}</h3>
            <p className='text-2xl font-bold text-purple-600'>{summary.by_status.active || 0}</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6'>
        {/* Seletor de empresa - apenas para usuários não CLIENT */}
        {!isClientUser && (
          <div className='mb-3'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              <Building2 className='w-4 h-4 inline mr-1' />
              {t('assets.company.label')}
            </label>
            <select
              className='px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={selectedCompany || ''}
              onChange={e => setSelectedCompany(e.target.value ? Number(e.target.value) : null)}
            >
              <option value=''>{t('assets.company.select')}</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            {selectedCompany && (
              <p className='text-sm text-blue-600 dark:text-blue-400 mt-1'>{t('assets.info.showingSelectedCompany')}</p>
            )}
          </div>
        )}

        {/* Informação da empresa para usuários CLIENT */}
        {isClientUser && user?.company && (
          <div className='mb-3'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              <Building2 className='w-4 h-4 inline mr-1' />
              {t('assets.company.label')}
            </label>
            <div className='px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg'>
              <span className='text-gray-900 dark:text-gray-100'>{user.company.name}</span>
              <p className='text-sm text-blue-600 dark:text-blue-400 mt-1'>{t('assets.info.showingYourCompany')}</p>
            </div>
          </div>
        )}
        <div className='flex flex-wrap gap-4'>
          <div className='flex-1 min-w-64'>
            <div className='relative'>
              <Search className='w-4 h-4 absolute left-3 top-3 text-gray-400' />
              <input
                type='text'
                placeholder={t('assets.filters.searchPlaceholder')}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                value={filters.search || ''}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>
          <div className="relative">
            <select
              className='px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={filters.asset_type || ''}
              onChange={e => setFilters({ ...filters, asset_type: (e.target.value as Asset['type']) || undefined })}
            >
              <option value=''>{t('assets.filters.allTypes')}</option>
              <option value='domain'>{t('assets.types.domain')}</option>
              <option value='subdomain'>{t('assets.types.subdomain')}</option>
              <option value='ip_address'>{t('assets.types.ip_address')}</option>
              <option value='email'>{t('assets.types.email')}</option>
              <option value='url'>{t('assets.types.url')}</option>
              <option value='social_media'>{t('assets.types.social_media')}</option>
              <option value='mobile_app'>{t('assets.types.mobile_app')}</option>
              <option value='certificate'>{t('assets.types.certificate')}</option>
              <option value='repository'>{t('assets.types.repository')}</option>
              <option value='file'>{t('assets.types.file')}</option>
              <option value='other'>{t('assets.types.other')}</option>
            </select>
            <svg
              className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
            </svg>
          </div>
          <div className='relative'>
            <select
              className='appearance-none px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as Asset['status'] || undefined })}
            >
              <option value=''>{t('assets.filters.allStatuses')}</option>
              <option value='active'>{t('assets.status.active')}</option>
              <option value='monitoring'>{t('assets.status.monitoring')}</option>
              <option value='inactive'>{t('assets.status.inactive')}</option>
              <option value='compromised'>{t('assets.status.compromised')}</option>
              <option value='unknown'>{t('assets.status.unknown')}</option>
            </select>
            <svg
              className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
            </svg>
          </div>
          <div className='relative'>
            <select
              className='appearance-none px-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              value={filters.criticality || ''}
              onChange={(e) => setFilters({ ...filters, criticality: e.target.value as Asset['criticality'] || undefined })}
            >
              <option value=''>{t('assets.filters.allCriticalities')}</option>
              <option value='critical'>{t('assets.criticality.critical')}</option>
              <option value='high'>{t('assets.criticality.high')}</option>
              <option value='medium'>{t('assets.criticality.medium')}</option>
              <option value='low'>{t('assets.criticality.low')}</option>
            </select>
            <svg
              className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
            </svg>
          </div>
        </div>
      </div>

      {/* Lista de Ativos */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden'>
        {loading ? (
          <div className='p-8 text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto'></div>
            <p className='mt-2 text-gray-600 dark:text-gray-300'>{t('assets.loading')}</p>
          </div>
        ) : assets.length === 0 ? (
          <div className='p-8 text-center'>
            <AlertTriangle className='w-12 h-12 text-gray-400 mx-auto mb-4' />
            <p className='text-gray-600 dark:text-gray-300'>{t('assets.empty')}</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.asset')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.type')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.status')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.criticality')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.createdAt')}
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('assets.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {assets.map(asset => (
                  <tr key={asset.id} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        <div className='flex-shrink-0 h-8 w-8 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg'>
                          {getAssetIcon(asset.type)}
                        </div>
                        <div className='ml-4'>
                          <div className='text-sm font-medium text-gray-900 dark:text-gray-100'>{asset.name}</div>
                          <div className='text-sm text-gray-500 dark:text-gray-400'>{asset.value}</div>
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                        {t(`assets.types.${asset.type}`)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        {getStatusIcon(asset.status)}
                        <span className='ml-2 text-sm text-gray-900 dark:text-gray-100 capitalize'>{t(`assets.status.${asset.status}`)}</span>
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCriticalityColor(asset.criticality)}`}
                      >
                        {asset.criticality}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                      {formatDate(new Date(asset.created_at))}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <button
                        onClick={() => {
                          setSelectedAsset(asset);
                          setShowEditModal(true);
                        }}
                        className='text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-3'
                      >
                        <Edit className='w-4 h-4' />
                      </button>
                      <button
                        onClick={() => handleDeleteAsset(asset.id)}
                        className='text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                      >
                        <Trash2 className='w-4 h-4' />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Criação */}
      {showCreateModal && (
        <AssetCreateModal
          companies={companies}
          selectedCompany={selectedCompany}
          user={user}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAsset}
        />
      )}

      {/* Modal de Edição */}
      {showEditModal && selectedAsset && (
        <AssetEditModal
          asset={selectedAsset}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAsset(null);
          }}
          onSubmit={data => handleUpdateAsset(selectedAsset.id, data)}
        />
      )}
    </div>
  );
};

// Modal de Criação
const AssetCreateModal: React.FC<{
  companies: Company[];
  selectedCompany: number | null;
  user: any;
  onClose: () => void;
  onSubmit: (data: AssetCreate) => void;
}> = ({ companies, selectedCompany, user, onClose, onSubmit }) => {
  const isClientUser = user?.role === UserRole.CLIENT;
  const defaultCompanyId = isClientUser
    ? user?.company_id
    : selectedCompany || (companies.length > 0 ? companies[0].id : 0);
  const [formData, setFormData] = useState<AssetCreate>({
    name: '',
    type: 'domain',
    value: '',
    description: '',
    criticality: 'medium',
    tags: [],
    company_id: defaultCompanyId,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md'>
        <h2 className='text-lg font-bold text-gray-900 dark:text-gray-100 mb-4'>Novo Ativo</h2>
        <form onSubmit={handleSubmit}>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Empresa</label>
            {isClientUser ? (
              <div className='w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg'>
                <span className='text-gray-900 dark:text-white'>{user?.company?.name}</span>
                <p className='text-sm text-blue-600 mt-1'>Ativo será criado para sua empresa</p>
              </div>
            ) : (
              <select
                required
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
                value={formData.company_id}
                onChange={e => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
              >
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Nome</label>
            <input
              type='text'
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Tipo</label>
            <select
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value as Asset['type'] })}
            >
              <option value='domain'>Domínio</option>
              <option value='subdomain'>Subdomínio</option>
              <option value='ip_address'>IP</option>
              <option value='email'>Email</option>
              <option value='url'>URL</option>
              <option value='social_media'>Mídia Social</option>
              <option value='mobile_app'>App Mobile</option>
              <option value='certificate'>Certificado</option>
              <option value='repository'>Repositório</option>
              <option value='file'>Arquivo</option>
              <option value='other'>Outro</option>
            </select>
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Valor</label>
            <input
              type='text'
              required
              placeholder='ex: exemplo.com.br'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.value}
              onChange={e => setFormData({ ...formData, value: e.target.value })}
            />
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Criticidade</label>
            <select
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.criticality}
              onChange={e => setFormData({ ...formData, criticality: e.target.value as Asset['criticality'] })}
            >
              <option value='low'>Baixa</option>
              <option value='medium'>Média</option>
              <option value='high'>Alta</option>
              <option value='critical'>Crítica</option>
            </select>
          </div>
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Descrição</label>
            <textarea
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500'
              rows={3}
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className='flex justify-end gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700'
            >
              Cancelar
            </button>
            <button type='submit' className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'>
              Criar Ativo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Edição
const AssetEditModal: React.FC<{
  asset: Asset;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ asset, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: asset.name,
    description: asset.description || '',
    criticality: asset.criticality,
    status: asset.status,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md'>
        <h2 className='text-lg font-bold text-gray-900 dark:text-gray-100 mb-4'>Editar Ativo</h2>
        <form onSubmit={handleSubmit}>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Nome</label>
            <input
              type='text'
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Status</label>
            <select
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value as Asset['status'] })}
            >
              <option value='active'>Ativo</option>
              <option value='monitoring'>Monitorando</option>
              <option value='inactive'>Inativo</option>
              <option value='compromised'>Comprometido</option>
              <option value='unknown'>Desconhecido</option>
            </select>
          </div>
          <div className='mb-4'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Criticidade</label>
            <select
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500'
              value={formData.criticality}
              onChange={e => setFormData({ ...formData, criticality: e.target.value as Asset['criticality'] })}
            >
              <option value='low'>Baixa</option>
              <option value='medium'>Média</option>
              <option value='high'>Alta</option>
              <option value='critical'>Crítica</option>
            </select>
          </div>
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>Descrição</label>
            <textarea
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500'
              rows={3}
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className='flex justify-end gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700'
            >
              Cancelar
            </button>
            <button type='submit' className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'>
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Assets;
